/**
 * NCR Detail Tab (Step 1) for NCR Edit.
 *
 * Stacked bordered cards grouping fields by concept (Customer & Supplier ·
 * Order Information · Reject Details & Action).
 */

import { Button } from '../../components/ui/button';
import { Save, Send } from 'lucide-react';
import { NonConformanceReport, UserProfile, SourceOfRejection, FaultCodeGroup, FaultCodeCategory } from '../../lib/api';
import { NCREditFormData } from './types';
import { CustomerSupplierSection } from './ncr-detail/CustomerSupplierSection';
import { OrderInformationSection } from './ncr-detail/OrderInformationSection';
import { RejectDetailsSection } from './ncr-detail/RejectDetailsSection';

interface Step1TabProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  saving: boolean;
  customerUsers: UserProfile[];
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  faultCategories: FaultCodeCategory[];
  /** Show the delivery Warehouse field. Caller gates on the schedule-lines
   *  tenant flag (PlatformSettings.dashboard_tabs.scheduled). */
  showWarehouse: boolean;
  /** Customer review 2026-07: customer-side viewers may confirm containment
   *  even after the NCR has moved past its editable stage. */
  canConfirmContainment?: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitDetail: () => void;
  onCloseNo8D: () => void;
}

export function NCRDetailTab({
  ncr,
  formData,
  canEdit,
  saving,
  customerUsers,
  sourcesOfRejection,
  faultGroups,
  faultCategories,
  showWarehouse,
  canConfirmContainment = false,
  onUpdateFormData,
  onSave,
  onSubmitDetail,
  onCloseNo8D,
}: Step1TabProps) {
  return (
    <>
      <div className="space-y-6">
        {/* ===== Section 1 — Customer & Supplier ===== */}
        <CustomerSupplierSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          onUpdateFormData={onUpdateFormData}
        />

        {/* ===== Section 2 — Order Information ===== */}
        <OrderInformationSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          customerUsers={customerUsers}
          showWarehouse={showWarehouse}
          onUpdateFormData={onUpdateFormData}
        />

        {/* ===== Section 3 — Reject Details & Action ===== */}
        <RejectDetailsSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          customerUsers={customerUsers}
          sourcesOfRejection={sourcesOfRejection}
          faultGroups={faultGroups}
          faultCategories={faultCategories}
          canConfirmContainment={canConfirmContainment}
          onUpdateFormData={onUpdateFormData}
        />
      </div>

      {/* Submit buttons below the grouped sections. Save is also available to a
          customer during containment (canConfirmContainment) so the manually
          entered containment quantities — incl. Current Stock — persist without
          the NCR being in draft. Submit / Close stay draft-only (canEdit). */}
      {(canEdit || canConfirmContainment) && (
        <div className="flex gap-2 mt-4">
          <Button onClick={onSave} disabled={saving} variant="outline">
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          {canEdit && (
            <>
              <Button onClick={onSubmitDetail} disabled={saving}>
                <Send className="h-4 w-4 mr-2" />
                Submit
              </Button>
              {formData.is_8d_required === false && (
                <Button onClick={onCloseNo8D} disabled={saving} variant="destructive">
                  Close - No 8D Required
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
